import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatPrice, resolvePhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CascadeSelect } from "@/components/CascadeSelect";
import { DateFieldFr, formatDateFr, formatDateTimeFr } from "@/components/DateFieldFr";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Megaphone, PlusCircle, Loader2, ShieldCheck, MapPin, CalendarDays, Package,
  Pause, Play, Trash2, ShoppingCart, Clock, ShieldAlert, CheckCircle2, Store, HandCoins, XCircle,
} from "lucide-react";

const STATUS_BADGE = {
  pending: { key: "offers.statusPending", cls: "bg-accent/15 text-accent-foreground", Icon: Clock },
  published: { key: "offers.statusPublished", cls: "bg-secondary/10 text-secondary", Icon: CheckCircle2 },
  suspended: { key: "offers.statusSuspended", cls: "bg-destructive/10 text-destructive", Icon: ShieldAlert },
  paused: { key: "offers.statusPaused", cls: "bg-muted text-muted-foreground", Icon: Pause },
};

const StatusBadge = ({ status }) => {
  const { t } = useT();
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return <Badge className={`${s.cls} border-0 gap-1`}><s.Icon className="h-3.5 w-3.5" />{t(s.key)}</Badge>;
};

// ---------- Buyer ----------
function BuyerOffers() {
  const { t } = useT();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(undefined); // undefined=loading, null=none
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [counterFor, setCounterFor] = useState(null); // response id
  const [counterPrice, setCounterPrice] = useState("");
  const [counterBusy, setCounterBusy] = useState(false);
  const [form, setForm] = useState({ category: "poulet-de-chair", quantity: 20, region: "", commune: "", neighborhood: "", needed_by: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => { api.get("/offers/mine").then((r) => setOffer(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, [load]);
  useAutoRefresh(load, 12000);

  const submit = async () => {
    if (!form.neighborhood.trim()) { toast.error(t("offers.neighborhoodRequired")); return; }
    setBusy(true);
    try {
      await api.post("/offers", {
        category: form.category, quantity: Number(form.quantity),
        commune: form.commune || null, neighborhood: form.neighborhood.trim(),
        region: form.region || null, needed_by: form.needed_by || null, note: form.note || null,
      });
      toast.success(t("offers.created"));
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const confirmIntent = async () => {
    try { const { data } = await api.post(`/offers/${offer.id}/confirm-intent`); setOffer(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const pauseToggle = async () => { await api.patch(`/offers/${offer.id}/pause`); load(); };
  const del = async () => { await api.delete(`/offers/${offer.id}`); toast.success(t("offers.deleted")); setOffer(null); };
  const choose = async (rid) => {
    try {
      const { data } = await api.post(`/offers/${offer.id}/choose/${rid}`);
      toast.success(t("offers.orderStarted"));
      navigate(`/commande/${data.order_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const sendCounter = async () => {
    const price = Number(counterPrice);
    if (!price || price <= 0) { toast.error(t("offers.counterInvalid")); return; }
    setCounterBusy(true);
    try {
      await api.post(`/offers/${offer.id}/responses/${counterFor}/counter`, { unit_price: price });
      toast.success(t("offers.counterSent"));
      setCounterFor(null); setCounterPrice(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setCounterBusy(false); }
  };

  if (offer === undefined) return <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>;

  return (
    <div>
      {!offer ? (
        <div className="text-center py-16 rounded-2xl bg-card border border-dashed border-border" data-testid="offer-empty">
          <Megaphone className="h-12 w-12 mx-auto text-primary" />
          <h2 className="mt-4 font-heading font-bold text-xl text-secondary">{t("offers.buyerEmptyTitle")}</h2>
          <p className="mt-2 text-muted-foreground max-w-md mx-auto text-sm">{t("offers.buyerEmptyText")}</p>
          <Button onClick={() => setOpen(true)} className="mt-6 rounded-full bg-primary hover:bg-primary/90" data-testid="new-offer-btn">
            <PlusCircle className="h-4 w-4 mr-2" /> {t("offers.create")}
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-hidden" data-testid="my-offer">
          <div className="flex items-center gap-4 p-5 border-b border-border">
            <img src={resolvePhoto(offer.category_image)} alt={offer.category_name} className="h-16 w-16 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading font-bold text-lg text-secondary">{offer.category_name}</h2>
                <StatusBadge status={offer.status} />
              </div>
              <p className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mt-1">
                <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {offer.quantity}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[offer.neighborhood, offer.commune].filter(Boolean).join(", ")}</span>
                {offer.needed_by && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {formatDateFr(offer.needed_by)}</span>}
              </p>
              {offer.note && <p className="text-sm text-foreground/70 mt-1 italic">“{offer.note}”</p>}
              <p className="text-xs text-muted-foreground mt-1.5" data-testid={`offer-dates-${offer.id}`}>
                {t("offers.createdAt")} : {formatDateTimeFr(offer.created_at)}
                {offer.updated_at && offer.updated_at !== offer.created_at ? ` · ${t("offers.updatedAt")} : ${formatDateTimeFr(offer.updated_at)}` : ""}
              </p>
            </div>
          </div>

          <div className="p-5">
            {offer.status === "pending" && (
              <div className="rounded-xl bg-accent/10 p-4 text-sm text-accent-foreground flex items-start gap-2" data-testid="offer-pending-note">
                <Clock className="h-4 w-4 mt-0.5 shrink-0" /> {t("offers.pendingNote")}
              </div>
            )}
            {offer.status === "suspended" && (
              <div className="rounded-xl bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {t("offers.suspendedNote")}
              </div>
            )}
            {offer.status === "paused" && (
              <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground flex items-center justify-between gap-2">
                <span className="flex items-center gap-2"><Pause className="h-4 w-4" /> {t("offers.pausedNote")}</span>
                <Button size="sm" variant="outline" onClick={pauseToggle} data-testid="resume-offer"><Play className="h-4 w-4 mr-1.5" />{t("offers.resume")}</Button>
              </div>
            )}

            {offer.status === "published" && !offer.intent_confirmed && (
              <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-5 text-center" data-testid="intent-gate">
                <ShieldCheck className="h-8 w-8 mx-auto text-primary" />
                <p className="mt-3 font-semibold text-secondary">
                  {offer.responses_count > 0 ? `${offer.responses_count} ${t("offers.responsesWaiting")}` : t("offers.noResponsesYet")}
                </p>
                <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">{t("offers.intentExplain")}</p>
                <Button onClick={confirmIntent} className="mt-4 rounded-full bg-primary hover:bg-primary/90" data-testid="confirm-intent-btn">
                  <ShieldCheck className="h-4 w-4 mr-2" /> {t("offers.confirmIntent")}
                </Button>
              </div>
            )}

            {offer.status === "published" && offer.intent_confirmed && (
              <div data-testid="offer-responses">
                <h3 className="font-heading font-semibold text-secondary mb-3">{t("offers.sellerResponses")} ({offer.responses?.length || 0})</h3>
                {(!offer.responses || offer.responses.length === 0) ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">{t("offers.noResponsesYet")}</p>
                ) : (
                  <div className="grid gap-3">
                    {offer.responses.map((r) => (
                      <div key={r.id} className="rounded-xl border border-border p-4 flex flex-col gap-3" data-testid={`response-${r.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-secondary flex items-center gap-1.5">
                              <Store className="h-4 w-4" /> {r.seller_name}
                              {r.seller_verified && <ShieldCheck className="h-4 w-4 text-secondary" title="Vérifié" />}
                            </p>
                            <p className="text-sm text-muted-foreground mt-0.5">{r.availability}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{r.quantity} × {formatPrice(r.unit_price)}</p>
                            <p className="font-heading font-extrabold text-primary text-lg">{formatPrice(r.price)}</p>
                          </div>
                        </div>

                        {r.counter_status === "pending" && (
                          <div className="rounded-lg bg-accent/15 border border-accent/30 px-3 py-2 text-sm text-accent-foreground flex items-center gap-2" data-testid={`counter-pending-${r.id}`}>
                            <Clock className="h-4 w-4 shrink-0" /> {t("offers.counterPending", { p: formatPrice(r.counter_price) })}
                          </div>
                        )}
                        {r.counter_status === "accepted" && (
                          <div className="rounded-lg bg-success/10 border border-success/30 px-3 py-2 text-sm text-success flex items-center gap-2" data-testid={`counter-accepted-${r.id}`}>
                            <CheckCircle2 className="h-4 w-4 shrink-0" /> {t("offers.counterAccepted")}
                          </div>
                        )}
                        {r.counter_status === "rejected" && (
                          <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground flex items-center gap-2" data-testid={`counter-rejected-${r.id}`}>
                            <ShieldAlert className="h-4 w-4 shrink-0" /> {t("offers.counterRejected")}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 justify-end">
                          {r.counter_status !== "pending" && (
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setCounterFor(r.id); setCounterPrice(String(r.unit_price)); }} data-testid={`counter-${r.id}`}>
                              <HandCoins className="h-4 w-4 mr-1.5" /> {t("offers.counterCta")}
                            </Button>
                          )}
                          <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" onClick={() => choose(r.id)} data-testid={`choose-${r.id}`}>
                            <ShoppingCart className="h-4 w-4 mr-1.5" /> {t("offers.chooseSeller")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-border">
              {offer.status === "published" && (
                <Button size="sm" variant="outline" onClick={pauseToggle} data-testid="pause-offer"><Pause className="h-4 w-4 mr-1.5" />{t("offers.pause")}</Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-destructive" data-testid="delete-offer"><Trash2 className="h-4 w-4 mr-1.5" />{t("offers.cancelOffer")}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-offer-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("offers.deleteConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("offers.deleteConfirmDesc")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-offer-cancel">{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={del} data-testid="delete-offer-confirm">{t("offers.cancelOffer")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("offers.createTitle")}</DialogTitle>
            <DialogDescription>{t("offers.createSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("offers.product")}</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="offer-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{t(`categories.${c.slug}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("offers.quantity")}</Label>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1.5" data-testid="offer-quantity" />
            </div>
            <div>
              <Label>{t("offers.deliveryLocation")}</Label>
              <CascadeSelect
                region={form.region} city={form.commune} neighborhood={form.neighborhood}
                onRegion={(v) => set("region", v)} onCity={(v) => set("commune", v)} onNeighborhood={(v) => set("neighborhood", v)}
                large={false} testid="offer-loc"
              />
            </div>
            <div>
              <Label>{t("offers.neededBy")}</Label>
              <div className="mt-1.5"><DateFieldFr value={form.needed_by} onChange={(v) => set("needed_by", v)} testid="offer-date" /></div>
            </div>
            <div>
              <Label>{t("offers.note")}</Label>
              <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder={t("offers.notePlaceholder")} rows={2} className="mt-1.5" data-testid="offer-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="offer-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("offers.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Counter-offer dialog */}
      <Dialog open={!!counterFor} onOpenChange={(v) => { if (!v) setCounterFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="counter-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary flex items-center gap-2"><HandCoins className="h-5 w-5 text-primary" /> {t("offers.counterTitle")}</DialogTitle>
            <DialogDescription>{t("offers.counterDesc")}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>{t("offers.yourCounterPrice")}</Label>
            <Input type="number" min={1} value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)} className="mt-1.5 h-12" placeholder="FCFA" data-testid="counter-price-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounterFor(null)}>{t("common.cancel")}</Button>
            <Button onClick={sendCounter} disabled={counterBusy} className="bg-primary hover:bg-primary/90" data-testid="counter-submit">
              {counterBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("offers.counterSend")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Seller ----------
function SellerOffers() {
  const { t } = useT();
  const [offers, setOffers] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState({ quantity: "", unit_price: "", availability: "" });

  const load = useCallback(() => { api.get("/offers").then((r) => setOffers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 12000);

  const openRespond = (o) => {
    setActive(o);
    setResp(o.my_response
      ? { quantity: String(o.my_response.quantity ?? o.quantity), unit_price: String(o.my_response.unit_price ?? ""), availability: o.my_response.availability }
      : { quantity: String(o.quantity), unit_price: "", availability: "" });
  };
  const respTotal = (Number(resp.quantity) || 0) * (Number(resp.unit_price) || 0);
  const decideCounter = async (offerId, respId, decision) => {
    try {
      await api.post(`/offers/${offerId}/responses/${respId}/counter/${decision}`);
      toast.success(decision === "accept" ? t("offers.counterAcceptedSeller") : t("offers.counterRejectedSeller"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const submit = async () => {
    if (!resp.quantity || !resp.unit_price || !resp.availability) { toast.error(t("offers.respRequired")); return; }
    setBusy(true);
    try {
      await api.post(`/offers/${active.id}/respond`, { quantity: Number(resp.quantity), unit_price: Number(resp.unit_price), availability: resp.availability });
      toast.success(t("offers.responseSent"));
      setActive(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {offers.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-card border border-dashed border-border" data-testid="seller-offers-empty">
          <Megaphone className="h-12 w-12 mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">{t("offers.sellerEmpty")}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {offers.map((o) => (
            <div key={o.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`offer-${o.id}`}>
              <div className="flex items-center gap-3">
                <img src={resolvePhoto(o.category_image)} alt={o.category_name} className="h-14 w-14 rounded-xl object-cover" />
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-secondary">{o.category_name}</h3>
                  <p className="text-sm text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                    <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {o.quantity}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[o.neighborhood, o.commune].filter(Boolean).join(", ")}</span>
                  </p>
                </div>
              </div>
              {o.needed_by && <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {t("offers.neededBy")}: {formatDateFr(o.needed_by)}</p>}
              {o.note && <p className="text-sm text-foreground/70 mt-1 italic">“{o.note}”</p>}
              <p className="text-xs text-muted-foreground mt-1.5" data-testid={`my-offer-dates-${o.id}`}>
                {t("offers.createdAt")} : {formatDateTimeFr(o.created_at)}
                {o.updated_at && o.updated_at !== o.created_at ? ` · ${t("offers.updatedAt")} : ${formatDateTimeFr(o.updated_at)}` : ""}
              </p>
              {o.my_response?.counter_status === "pending" && (
                <div className="mt-3 rounded-xl border-2 border-primary/30 bg-primary/5 p-3" data-testid={`seller-counter-${o.id}`}>
                  <p className="text-sm font-semibold text-secondary flex items-center gap-1.5">
                    <HandCoins className="h-4 w-4 text-primary" /> {t("offers.counterReceived", { p: formatPrice(o.my_response.counter_price) })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("offers.counterVsYours", { p: formatPrice(o.my_response.unit_price) })}</p>
                  <div className="flex gap-2 mt-2.5">
                    <Button size="sm" className="bg-success hover:bg-success/90 text-white rounded-full" onClick={() => decideCounter(o.id, o.my_response.id, "accept")} data-testid={`counter-accept-${o.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> {t("offers.accept")}
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive rounded-full" onClick={() => decideCounter(o.id, o.my_response.id, "reject")} data-testid={`counter-reject-${o.id}`}>
                      <XCircle className="h-4 w-4 mr-1.5" /> {t("offers.reject")}
                    </Button>
                  </div>
                </div>
              )}
              {o.my_response?.counter_status === "accepted" && (
                <div className="mt-3 rounded-lg bg-success/10 border border-success/30 px-3 py-2 text-sm text-success flex items-center gap-2" data-testid={`seller-counter-accepted-${o.id}`}>
                  <CheckCircle2 className="h-4 w-4 shrink-0" /> {t("offers.counterAcceptedSeller")}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between gap-2">
                {o.my_response
                  ? <Badge className="bg-secondary/10 text-secondary border-0">{t("offers.youProposed")}: {formatPrice(o.my_response.price)}</Badge>
                  : <span className="text-xs text-muted-foreground">{o.responses_count} {t("offers.responsesLabel")}</span>}
                <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" onClick={() => openRespond(o)} data-testid={`respond-${o.id}`}>
                  {o.my_response ? t("offers.editResponse") : t("offers.respond")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-md" data-testid="respond-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("offers.respondTitle")}</DialogTitle>
            <DialogDescription>{active && `${active.category_name} · ${active.quantity} · ${[active.neighborhood, active.commune].filter(Boolean).join(", ")}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("offers.quantity")}</Label>
                <Input type="number" min={1} value={resp.quantity} onChange={(e) => setResp((r) => ({ ...r, quantity: e.target.value }))} className="mt-1.5" data-testid="resp-quantity" />
              </div>
              <div>
                <Label>{t("offers.unitPrice")}</Label>
                <Input type="number" min={1} value={resp.unit_price} onChange={(e) => setResp((r) => ({ ...r, unit_price: e.target.value }))} className="mt-1.5" placeholder="FCFA" data-testid="resp-unit-price" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/20 px-4 py-3" data-testid="resp-total">
              <span className="text-sm font-semibold text-secondary">{t("offers.total")}</span>
              <span className="font-heading font-extrabold text-primary text-lg">{formatPrice(respTotal)}</span>
            </div>
            <div>
              <Label>{t("offers.availability")}</Label>
              <Textarea value={resp.availability} onChange={(e) => setResp((r) => ({ ...r, availability: e.target.value }))} rows={2} className="mt-1.5" placeholder={t("offers.availabilityPlaceholder")} data-testid="resp-availability" />
            </div>
            <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />{t("offers.noPhoneWarning")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="resp-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("offers.sendResponse")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Offers() {
  const { t } = useT();
  const { user } = useAuth();
  const isSeller = user?.role === "seller";

  return (
    <div className="container-page py-10">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-secondary flex items-center gap-2">
          <Megaphone className="h-7 w-7 text-primary" /> {t("offers.title")}
        </h1>
        <p className="text-muted-foreground mt-1">{isSeller ? t("offers.sellerSubtitle") : t("offers.buyerSubtitle")}</p>
      </div>
      {isSeller ? <SellerOffers /> : <BuyerOffers />}
    </div>
  );
}
